import { openDB } from 'idb';

const DB_NAME = 'getCodeDB';
const STORE_NAME = 'codeHistory';
const DB_VERSION = 1;

const initDB = async () => {
    const db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: 'code' });
                store.createIndex('timestamp', 'timestamp', { unique: false });
            }
        },
    });
    return db;
};

// Save history to both IndexedDB and localStorage for backward compatibility
export const saveToHistory = async (codeData) => {
    try {
        const db = await initDB();
        const timestamp = Date.now();
        const historyItem = { ...codeData, timestamp };

        // Use put instead of add to update existing entries
        await db.put(STORE_NAME, historyItem);

        // Get all items, sort them, and update localStorage
        const allItems = await db.getAll(STORE_NAME);
        const sortedItems = allItems.sort((a, b) => b.timestamp - a.timestamp);
        
        // Keep only the last 50 items
        const limitedItems = sortedItems.slice(0, 50);
        
        // Update localStorage for backward compatibility
        localStorage.setItem('codeHistory', JSON.stringify(limitedItems));

        // Dispatch event for UI update
        window.dispatchEvent(new Event('historyUpdated'));
    } catch (error) {
        console.error('Error saving to history:', error);
    }
};

export const getHistory = async () => {
    try {
        const db = await initDB();
        const allHistory = await db.getAll(STORE_NAME);
        
        // Sort by timestamp in descending order (newest first)
        const sortedHistory = allHistory.sort((a, b) => b.timestamp - a.timestamp);
        
        // Keep only the last 50 items
        const limitedHistory = sortedHistory.slice(0, 50);
        
        return limitedHistory;
    } catch (error) {
        console.error('Error getting history:', error);
        // Fallback to localStorage if IndexedDB fails
        const localHistory = JSON.parse(localStorage.getItem('codeHistory') || '[]');
        return localHistory.sort((a, b) => b.timestamp - a.timestamp);
    }
};

export const clearHistory = async () => {
    try {
        const db = await initDB();
        await db.clear(STORE_NAME);
        localStorage.removeItem('codeHistory');
        window.dispatchEvent(new Event('historyUpdated'));
    } catch (error) {
        console.error('Error clearing history:', error);
    }
};

// Migration function to move data from localStorage to IndexedDB
export const migrateFromLocalStorage = async () => {
    try {
        const existingHistory = JSON.parse(localStorage.getItem('codeHistory') || '[]');
        if (existingHistory.length > 0) {
            const db = await initDB();
            
            // Process items one by one to ensure proper timestamp ordering
            for (const item of existingHistory) {
                // Only migrate if the item has a code
                if (item.code) {
                    await db.put(STORE_NAME, {
                        ...item,
                        timestamp: item.timestamp || Date.now()
                    });
                }
            }
            
            // Update localStorage with cleaned data
            const allItems = await db.getAll(STORE_NAME);
            const sortedItems = allItems.sort((a, b) => b.timestamp - a.timestamp);
            localStorage.setItem('codeHistory', JSON.stringify(sortedItems));
        }
    } catch (error) {
        console.error('Error migrating history:', error);
    }
}; 